// Shared by React dialogs and the imperative alerts in ui.js.
const dialogs = [];
const activeDialogs = new WeakMap();
const returnTargets = new WeakMap();
const focusable = 'a[href], button, input, select, textarea, [tabindex]';
let nextId = 0;
let previousOverflow;

export function activateDialog(overlay, { onClose, initialFocus } = {}) {
  activeDialogs.get(overlay)?.();
  let previousFocus = document.activeElement;
  const previousDialog = dialogs.at(-1);
  const fallbackTargets = previousDialog?.contains(previousFocus) ? returnTargets.get(previousDialog) || [] : [];
  returnTargets.set(overlay, [previousFocus, ...fallbackTargets]);
  let closeHandler = onClose;
  const id = overlay.dataset.dialogId || `dialog-${++nextId}`;
  overlay.dataset.dialogId = id;
  overlay.tabIndex = -1;
  const heading = overlay.querySelector('h3');
  if (heading) {
    heading.id ||= `${id}-title`;
    overlay.setAttribute('aria-labelledby', heading.id);
  }
  if (!dialogs.length) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  dialogs.push(overlay);
  const scopes = () => [overlay, ...document.querySelectorAll('[data-dialog-owner]')]
    .filter(el => el === overlay || el.dataset.dialogOwner === id);
  const contains = node => scopes().some(el => el.contains(node));
  const targets = () => scopes().flatMap(el => [...el.querySelectorAll(focusable)])
    .filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[inert]') && el.getClientRects().length);
  const focusFirst = () => (targets()[0] || overlay).focus({ preventScroll: true });
  function onKeyDown(event) {
    if (dialogs.at(-1) !== overlay || event.defaultPrevented) return;
    if (event.key === 'Escape') {
      // A portaled combo handles its own Escape before the dialog does.
      if (event.target.closest('[data-dialog-owner]')?.dataset.dialogOwner === id) return;
      if (closeHandler) { event.preventDefault(); closeHandler(); }
    } else if (event.key === 'Tab') {
      const items = targets();
      const index = items.indexOf(document.activeElement);
      if (!items.length || index < 0 || (event.shiftKey ? index === 0 : index === items.length - 1)) {
        event.preventDefault();
        (event.shiftKey ? items.at(-1) || overlay : items[0] || overlay).focus();
      }
    }
  }
  function onFocus(event) {
    if (dialogs.at(-1) === overlay && !contains(event.target)) focusFirst();
  }
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('focusin', onFocus);
  (initialFocus || targets()[0] || overlay).focus({ preventScroll: true });
  const deactivate = () => {
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('focusin', onFocus);
    const wasTop = dialogs.at(-1) === overlay;
    const index = dialogs.indexOf(overlay);
    if (index >= 0) dialogs.splice(index, 1);
    activeDialogs.delete(overlay);
    const restoreTarget = returnTargets.get(overlay)?.find(target => target?.isConnected && !target.closest('[inert]'));
    returnTargets.delete(overlay);
    if (!dialogs.length) document.body.style.overflow = previousOverflow;
    previousFocus = null;
    closeHandler = null;
    if (wasTop && restoreTarget?.isConnected) restoreTarget.focus({ preventScroll: true });
  };
  activeDialogs.set(overlay, deactivate);
  return deactivate;
}
